function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { findFirstFocusableNode } from '../../utilities/focus.js';
import { useToggle } from '../../utilities/use-toggle.js';
import { classNames } from '../../utilities/css.js';
import styles from './Tooltip.scss.js';
import { useEphemeralPresenceManager } from '../../utilities/ephemeral-presence-manager/hooks.js';
import { TooltipOverlay } from './components/TooltipOverlay/TooltipOverlay.js';
import { useUniqueId } from '../../utilities/unique-id/hooks.js';
import { Portal } from '../Portal/Portal.js';
var HOVER_OUT_TIMEOUT = 150;

function Tooltip(_ref) {
  var children = _ref.children,
      content = _ref.content,
      dismissOnMouseOut = _ref.dismissOnMouseOut,
      originalActive = _ref.active,
      hoverDelay = _ref.hoverDelay,
      _ref$preferredPositio = _ref.preferredPosition,
      preferredPosition = _ref$preferredPositio === void 0 ? 'above' : _ref$preferredPositio,
      _ref$activatorWrapper = _ref.activatorWrapper,
      activatorWrapper = _ref$activatorWrapper === void 0 ? 'span' : _ref$activatorWrapper,
      accessibilityLabel = _ref.accessibilityLabel,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? 'default' : _ref$width,
      _ref$padding = _ref.padding,
      padding = _ref$padding === void 0 ? 'default' : _ref$padding,
      _ref$borderRadius = _ref.borderRadius,
      borderRadius = _ref$borderRadius === void 0 ? '1' : _ref$borderRadius,
      zIndexOverride = _ref.zIndexOverride,
      hasUnderline = _ref.hasUnderline,
      persistOnClick = _ref.persistOnClick,
      onOpen = _ref.onOpen,
      onClose = _ref.onClose;
  var WrapperComponent = activatorWrapper;

  var _useToggle = useToggle(Boolean(originalActive)),
      active = _useToggle.value,
      setActiveTrue = _useToggle.setTrue,
      handleBlur = _useToggle.setFalse;

  var _useToggle2 = useToggle(Boolean(originalActive) && Boolean(persistOnClick)),
      persist = _useToggle2.value,
      togglePersisting = _useToggle2.toggle;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      activatorNode = _useState2[0],
      setActivatorNode = _useState2[1];

  var _useEphemeralPresence = useEphemeralPresenceManager(),
      presenceList = _useEphemeralPresence.presenceList,
      addPresence = _useEphemeralPresence.addPresence,
      removePresence = _useEphemeralPresence.removePresence;

  var id = useUniqueId('TooltipContent');
  var activatorContainer = useRef(null);
  var mouseEntered = useRef(false);

  var _useState3 = useState(Boolean(!originalActive)),
      _useState4 = _slicedToArray(_useState3, 2),
      shouldAnimate = _useState4[0],
      setShouldAnimate = _useState4[1];

  var hoverDelayTimeout = useRef(null);
  var hoverOutTimeout = useRef(null);
  var handleFocus = useCallback(function () {
    if (originalActive !== false) {
      setActiveTrue();
    }
  }, [originalActive, setActiveTrue]);
  useEffect(function () {
    var firstFocusable = activatorContainer.current ? findFirstFocusableNode(activatorContainer.current) : null;
    var accessibilityNode = firstFocusable || activatorContainer.current;
    if (!accessibilityNode) return;
    accessibilityNode.tabIndex = 0;
    accessibilityNode.setAttribute('aria-describedby', id);
    accessibilityNode.setAttribute('data-polaris-tooltip-activator', 'true');
  }, [id, children]);
  useEffect(function () {
    return function () {
      if (hoverDelayTimeout.current) {
        clearTimeout(hoverDelayTimeout.current);
      }

      if (hoverOutTimeout.current) {
        clearTimeout(hoverOutTimeout.current);
      }
    };
  }, []);
  var handleOpen = useCallback(function () {
    setShouldAnimate(!presenceList.tooltip && !active);
    onOpen === null || onOpen === void 0 ? void 0 : onOpen();
    addPresence('tooltip');
  }, [addPresence, presenceList.tooltip, onOpen, active]);
  var handleClose = useCallback(function () {
    onClose === null || onClose === void 0 ? void 0 : onClose();
    setShouldAnimate(false);
    hoverOutTimeout.current = setTimeout(function () {
      removePresence('tooltip');
    }, HOVER_OUT_TIMEOUT);
  }, [removePresence, onClose]);
  var handleKeyUp = useCallback(function (event) {
    if (event.key !== 'Escape') return;
    handleClose === null || handleClose === void 0 ? void 0 : handleClose();
    handleBlur();
    persistOnClick && togglePersisting();
  }, [handleBlur, handleClose, persistOnClick, togglePersisting]);
  var portal = activatorNode ? /*#__PURE__*/React.createElement(Portal, {
    idPrefix: "tooltip"
  }, /*#__PURE__*/React.createElement(TooltipOverlay, {
    id: id,
    preferredPosition: preferredPosition,
    activator: activatorNode,
    active: active,
    accessibilityLabel: accessibilityLabel,
    onClose: noop,
    preventInteraction: dismissOnMouseOut,
    width: width,
    padding: padding,
    borderRadius: borderRadius,
    zIndexOverride: zIndexOverride,
    instant: !shouldAnimate
  }, content)) : null;
  var wrapperClassNames = classNames(activatorWrapper === 'div' && styles.TooltipContainer, hasUnderline && styles.HasUnderline);
  return /*#__PURE__*/React.createElement(WrapperComponent, {
    onFocus: function onFocus() {
      handleOpen();
      handleFocus();
    },
    onBlur: function onBlur() {
      handleClose();
      handleBlur();

      if (persistOnClick) {
        togglePersisting();
      }
    },
    onMouseLeave: handleMouseLeave,
    onMouseOver: handleMouseEnterFix,
    onMouseDown: persistOnClick ? togglePersisting : undefined,
    ref: setActivator,
    onKeyUp: handleKeyUp,
    className: wrapperClassNames
  }, children, portal);

  function setActivator(node) {
    var activatorContainerRef = activatorContainer;

    if (node == null) {
      activatorContainerRef.current = null;
      setActivatorNode(null);
      return;
    }

    node.firstElementChild instanceof HTMLElement && setActivatorNode(node.firstElementChild);
    activatorContainerRef.current = node;
  }

  function handleMouseEnter() {
    mouseEntered.current = true;

    if (hoverDelay && !presenceList.tooltip) {
      hoverDelayTimeout.current = setTimeout(function () {
        handleOpen();
        handleFocus();
      }, hoverDelay);
    } else {
      handleOpen();
      handleFocus();
    }
  }

  function handleMouseLeave() {
    if (hoverDelayTimeout.current) {
      clearTimeout(hoverDelayTimeout.current);
      hoverDelayTimeout.current = null;
    }

    mouseEntered.current = false;
    handleClose();

    if (!persist) {
      handleBlur();
    }
  } // https://github.com/facebook/react/issues/10109
  // Mouseenter event not triggered when cursor moves from disabled button


  function handleMouseEnterFix() {
    !mouseEntered.current && handleMouseEnter();
  }
}

function noop() {}

export { Tooltip };